import { Card, CardContent, Grid, Stack, useTheme } from '@mui/material';
import { MprcTypography } from 'components';
import { DevPlanViewDto } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import React from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    viewData: DevPlanViewDto;
}

////////////////////////////////////////////////////////////////////////////

export const DevPlanCardStatus: React.FC<IProps> = ({ viewData }) => {

    const theme = useTheme();
    const { t } = useTranslation("lms");

    const summary = viewData.summary;
    const isAchieved = viewData.archivedOn != null;
    const isDelayed = summary.totalDelayed > 0;

    var statusColor = isAchieved
        ? theme.palette.warning.main
        : isDelayed
            ? theme.palette.error.main
            : viewData.completionPercent == 100
                ? theme.palette.success.main
                : theme.palette.primary.main;

    ///
    const renderCompletedPercent = () => {
        return (
            <Stack direction="column" alignItems="center" justifyContent="center" height="100%">
                <MprcTypography variant="h3" color="muted" textAlign="center" fontSize={32}>
                    {Formaters.localeNumber(viewData.completionPercent)}%
                </MprcTypography>
                <MprcTypography variant="h2" color="muted" textAlign="center" fontSize={12} textTransform="uppercase">
                    {t("DevPlan.Edit.View.Status.CompletedText")}
                </MprcTypography>
            </Stack>
        );
    };

    ///
    const renderProgress = () => {
        return (
            <Grid container direction="column" justifyContent="space-between" height="100%" minWidth="100%">
                <Grid size="grow">
                    {renderCompletedPercent()}
                </Grid>
                {
                    summary.totalPending > 0 &&
                    <Grid size="grow">
                        <Stack direction="column" alignItems="center" justifyContent="end" height="100%">
                            <MprcTypography variant='caption' component='p' color="muted" textAlign="center">
                                {t("DevPlan.Edit.View.Status.ItemsPending_L1", { count: summary.totalPending })}<br />
                                {t("DevPlan.Edit.View.Status.ItemsPending_L2")}
                            </MprcTypography>
                        </Stack>
                    </Grid>
                }
            </Grid>
        );
    };

    ///
    const renderDelayed = () => {
        return (
            <Grid container direction="column" justifyContent="space-between" height="100%" minWidth="100%">
                <Grid size="grow">
                    <Stack direction="column" alignItems="center" justifyContent="center" height="100%">
                        <MprcTypography variant="h3" color="error" textAlign="center" fontSize={32}>{summary.totalDelayed}</MprcTypography>
                        <MprcTypography variant="h2" color="error" textAlign="center" fontSize={12} textTransform="uppercase">
                            {t("DevPlan.Edit.View.Status.DelayActivityText", { count: summary.totalDelayed })}
                        </MprcTypography>
                    </Stack>
                </Grid>
                <Grid size="grow">
                    {renderCompletedPercent()}
                </Grid>
            </Grid>
        );
    };

    ///
    const renderArchived = () => {
        return (
            <Grid container direction="column" justifyContent="space-between" height="100%" minWidth="100%">
                <Grid size="grow">
                    <Stack direction="column" alignItems="center" justifyContent="center" height="100%">
                        <MprcTypography variant="h3" color="warning" textAlign="center" fontSize={18} textTransform="uppercase">
                            {t("DevPlan.Edit.View.Status.ArchivedText")}
                        </MprcTypography>
                        <MprcTypography variant="h2" color="warning" textAlign="center" fontSize={12} textTransform="uppercase">
                            {Formaters.performaDate(viewData.archivedOn, { excludeTime: true, preposition: "none" })}
                        </MprcTypography>
                    </Stack>
                </Grid>
                <Grid size="grow">
                    {renderCompletedPercent()}
                </Grid>
            </Grid>
        );
    };

    return (
        <Card
            sx={{
                minWidth: "140px",
                maxWidth: "140px",
                minHeight: "100px",
                borderTop: `12px solid ${statusColor}`,
                height: "calc(100% - 12px)",
            }}
        >
            <CardContent sx={{ paddingX: 1, paddingY: 1, height: "calc(100% - 24px)" }}>
                {
                    isAchieved ? renderArchived() :
                        isDelayed ? renderDelayed() :
                            renderProgress()
                }
            </CardContent>
        </Card>
    );
};
